/**
 * LocalStorage backup manager for NewsQuasar.
 *
 * Uses a shared article pool keyed by URL to avoid duplicate storage.
 * Each backup entry references articles by URL, so the same article
 * can appear in multiple backup keys (latest, category, search) while
 * being stored only once.
 *
 * Storage shape:
 *   {
 *     articles: { [url]: articleObject },
 *     backups:  { [key]: { timestamp, urls: string[] } }
 *   }
 *
 * @module utils/backup
 */

const STORAGE_KEY = 'newsquasar_backup';
const MAX_ARTICLES = 500;
const BACKUP_TTL = 24 * 60 * 60 * 1000; // 24 hours

// ── Pool read/write ────────────────────────────────────────────────

const loadPool = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { articles: {}, backups: {} };
    const parsed = JSON.parse(raw);
    return {
      articles: parsed.articles || {},
      backups: parsed.backups || {},
    };
  } catch {
    return { articles: {}, backups: {} };
  }
};

const savePool = (pool) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(pool));
  } catch {
    // localStorage full or unavailable — silently degrade
  }
};

// ── Dedup + prune ──────────────────────────────────────────────────

const pruneOldArticles = (pool) => {
  const now = Date.now();

  // Remove backup entries older than BACKUP_TTL
  Object.keys(pool.backups).forEach((key) => {
    if (now - pool.backups[key].timestamp > BACKUP_TTL) {
      delete pool.backups[key];
    }
  });

  // Collect all URLs referenced by any active backup
  const referenced = new Set();
  Object.values(pool.backups).forEach((entry) => {
    entry.urls.forEach((url) => referenced.add(url));
  });

  // Remove unreferenced articles
  Object.keys(pool.articles).forEach((url) => {
    if (!referenced.has(url)) {
      delete pool.articles[url];
    }
  });

  // If still over limit, remove oldest unreferenced by date
  if (Object.keys(pool.articles).length > MAX_ARTICLES) {
    const entries = Object.entries(pool.articles);
    entries.sort((a, b) => (b[1].date || '').localeCompare(a[1].date || ''));

    while (entries.length > MAX_ARTICLES) {
      const [url] = entries.pop();
      if (!referenced.has(url)) {
        delete pool.articles[url];
      }
    }
  }
};

// ── Public API ─────────────────────────────────────────────────────

/**
 * Save articles to a backup key, deduplicating by URL.
 *
 * @param {string} key - Cache key (e.g. "latest:en:10", "cat:technology:10").
 * @param {Array<Object>} newArticles - Normalized articles to save.
 */
export const saveBackup = (key, newArticles) => {
  if (!key || !Array.isArray(newArticles) || newArticles.length === 0) return;

  const pool = loadPool();

  // Add new articles to pool (skip if URL already exists)
  newArticles.forEach((article) => {
    if (article.url && !pool.articles[article.url]) {
      pool.articles[article.url] = article;
    }
  });

  // Merge URLs: existing + new, deduplicated via Set
  const existingUrls = pool.backups[key]?.urls || [];
  const newUrls = newArticles.map((a) => a.url).filter(Boolean);
  const mergedUrls = [...new Set([...existingUrls, ...newUrls])];

  pool.backups[key] = { timestamp: Date.now(), urls: mergedUrls };

  // Prune if pool is too large
  if (Object.keys(pool.articles).length > MAX_ARTICLES) {
    pruneOldArticles(pool);
  }

  savePool(pool);
};

/**
 * Load backed-up articles for a given key.
 *
 * @param {string} key - Cache key.
 * @returns {{ articles: Array<Object>, timestamp: number } | null}
 */
export const loadBackup = (key) => {
  const pool = loadPool();
  const backup = pool.backups[key];
  if (!backup) return null;

  // Resolve URLs to article objects, filtering out any that were pruned
  const articles = backup.urls
    .map((url) => pool.articles[url])
    .filter(Boolean);

  if (articles.length === 0) return null;

  return { articles, timestamp: backup.timestamp };
};

/**
 * Check if a backup exists for a given key.
 *
 * @param {string} key
 * @returns {boolean}
 */
export const hasBackup = (key) => {
  const pool = loadPool();
  const backup = pool.backups[key];
  return !!(backup && backup.urls.length > 0);
};

/**
 * Get human-readable age of a backup.
 *
 * @param {string} key
 * @returns {string | null} e.g. "2 hours ago", "yesterday", or null if no backup.
 */
export const getBackupAge = (key) => {
  const pool = loadPool();
  const backup = pool.backups[key];
  if (!backup) return null;

  const diff = Date.now() - backup.timestamp;
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(hours / 24);

  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  if (days === 1) return 'yesterday';
  return `${days} days ago`;
};

/**
 * Clear all backup data from localStorage.
 */
export const clearBackup = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // silently ignore
  }
};
